import React, { useEffect, useState } from "react";

import * as yup from "yup";
import DoneIcon from "@mui/icons-material/Done";
import { Box, Button, CircularProgress, Modal, Typography } from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import { isNull, omit } from "lodash";
import { useUserActions } from "store";
import {
  IHobby,
  IIndustryExpertise,
  IInterests,
  IProfileAssetAllocation,
  IProfileAssetClass,
  IProfileData,
  IProfileIndustryData,
  IProfileInvestment,
  IProfileMetadata,
  ISocialAssetClassesMeta,
  IUpdateProfileAssetClasses,
  IUpdateProfileData,
  IUpdateProfileIndustry,
  IUpdateProfileUser
} from "store/types/user/social";
import { toastify } from "utils/tyostify";

import { AssetAllocation } from "./Asset-allocation/AssetAllocation";
import { AssetClasses } from "./Asset-classes/AssetClasses";
import { Chips } from "./Chips/Chips";
import { IndustryExpertise } from "./Industry-expertise/IndustryExpertise";
import { NotableInvestments } from "./Notable-investments/NotableInvestments";
import { UserDetails } from "./User-details/UserDetails";

interface ISocialEditWindowProps {
  userId: number;
  isOpen: boolean;
  onClose: () => void;
  profileData: IProfileData;
  refetchUserData: () => Promise<void>;
  profileMetadata: IProfileMetadata;
  updateProfile: (data: IUpdateProfileData) => Promise<void>;
}
export interface IFormikValues {
  [key: string]: any;
  details: IUpdateProfileUser;
  industry_expertises: IProfileIndustryData[];
  hobbies: IHobby[];
  interests: IInterests[];
  asset_classes: IProfileAssetClass[];
  investments: IProfileInvestment[];
  asset_allocations: IProfileAssetAllocation[];
}

export const EditWindow: React.FC<ISocialEditWindowProps> = ({
  userId,
  isOpen,
  onClose,
  profileData,
  refetchUserData,
  profileMetadata,
  updateProfile
}) => {
  const [saveDataLoader, setSaveDataLoader] = useState(false);
  const [openedStage, setOpenedStage] = useState<string[]>([]);
  const { updateAvatarAction } = useUserActions();

  //this useEffect needs for update formik after closing window or updating profile data
  useEffect(() => {
    const updatedExpertises = profileMetadata?.industry_expertises.map((item: IIndustryExpertise) => {
      const matchingItem = profileData?.industry_expertises.find(
        (secondItem: IProfileIndustryData) => secondItem.id === item.id
      );

      if (matchingItem) {
        return { ...item, ...matchingItem };
      }
      return {
        ...item,
        operational: false,
        investment: false,
        learn_more: false
      };
    });

    const updatedAssetClasses = profileMetadata?.asset_classes.map((item: ISocialAssetClassesMeta) => {
      const matchingItem = Object.values(profileData?.asset_classes)
        .flat()
        .find((secondItem: IProfileAssetClass) => secondItem.id === item.id);
      if (matchingItem) {
        return { ...item, ...matchingItem };
      }

      return {
        ...item,
        investment: false,
        learn_more: false
      };
    });

    setValues({
      details: { ...profileData.details },
      industry_expertises: updatedExpertises,
      hobbies: [...profileData.hobbies],
      interests: [...profileData.interests],
      asset_classes: updatedAssetClasses,
      investments: [...profileData.investments],
      asset_allocations: [...profileData.asset_allocations.filter((item) => item.id !== null)]
    });
  }, [isOpen]);

  useEffect(() => {
    setOpenedStage(["1"]);
  }, [isOpen, profileData]);

  const initialValues: IFormikValues = {
    details: { ...profileData.details },
    // industry_expertises will update after useEffect
    industry_expertises: [],
    hobbies: [...profileData.hobbies],
    interests: [...profileData.interests],
    // asset_classes will update after useEffect
    asset_classes: [],
    investments: [...profileData.investments],
    asset_allocations: [...profileData.asset_allocations.filter((item) => item.id !== null)]
  };

  const formik = useFormik({
    initialValues: initialValues as IFormikValues,
    validationSchema: yup.object().shape({
      details: yup.object().shape({
        first_name: yup.string().trim().required("First name is required"),
        last_name: yup.string().trim().required("Last name is required"),
        company_name: yup.string().trim().required("Company is required")
      })
    }),
    onSubmit: async (values) => {
      setSaveDataLoader(true);
      const hobbiesPayload = values.hobbies.map((item) => item.id);
      const investmentsPayload = values.investments.map((item) => item.company_name);
      const interestsPayload = values.interests.map((item) => item.id);

      const industryExpertisesPayload: IUpdateProfileIndustry[] = values.industry_expertises.map(
        (item: IUpdateProfileIndustry) => {
          return {
            id: item.id,
            operational: item.operational || false,
            investment: item.investment || false,
            learn_more: item.learn_more || false
          };
        }
      );

      const assetClassPayload: IUpdateProfileAssetClasses[] = values.asset_classes.map((item) => {
        return {
          id: item.id,
          investment: item.investment || false,
          learn_more: item.learn_more || false
        };
      });
      const assetAllocationPayload = values.asset_allocations
        .filter((item) => item.percentage > 0)
        .map((item) => {
          return {
            id: item.id,
            percentage: item.percentage,
            direct: item.direct,
            funds: item.funds,
            splittable: item.splittable
          };
        });

      const { photo: newAvatar } = values.details;

      if (isNull(newAvatar) || newAvatar) {
        try {
          await updateAvatarAction(userId, newAvatar);
        } catch (_err) {
          setSaveDataLoader(false);
        }
      }

      await updateProfile({
        userId,
        //return deatails without photo
        details: omit(values.details, ["photo"]),
        industry_expertises: industryExpertisesPayload,
        asset_classes: assetClassPayload,
        asset_allocations: assetAllocationPayload,
        hobbies: hobbiesPayload,
        interests: interestsPayload,
        investments: investmentsPayload
      })
        .catch((err) => {
          toastify({ message: "Something when wrong", type: "error" });
          console.log(err);
        })
        .finally(() => {
          setSaveDataLoader(false);
          onClose();
        });
      await refetchUserData();
    }
  });

  const { setValues, handleSubmit, values, isValid } = formik;

  const tabs = [
    {
      stageNumber: "1",
      formikKey: "details",
      title: "User Details",
      component: <UserDetails />
    },
    {
      stageNumber: "2",
      formikKey: "asset_allocations",
      title: "Asset Allocation",
      component: <AssetAllocation availableAllocations={profileMetadata.asset_allocations} />
    },
    {
      stageNumber: "3",
      formikKey: "industry_expertises",
      title: "Industry Expertise",
      component: <IndustryExpertise />,
      description: "Select industries and the type of expertise you have in each"
    },
    {
      stageNumber: "4",
      formikKey: "hobbies",
      title: "Hobbies",
      component: <Chips componentName="hobbies" componentArrayValue={profileMetadata?.hobbies} />
    },
    {
      stageNumber: "5",
      formikKey: "asset_classes",
      title: "Asset Classes",
      component: <AssetClasses />
    },
    {
      stageNumber: "6",
      formikKey: "investments",
      title: "Investments",
      component: <NotableInvestments />,
      description: "Add notable investments to your profile."
    },
    {
      stageNumber: "7",
      formikKey: "interests",
      title: "Philanthropic Interests",
      component: <Chips componentName="interests" componentArrayValue={profileMetadata?.interests} />,
      description: "Used to personalize your network and opportunity matching (choose all that apply)."
    }
  ];

  const handleClick = (value: string) => {
    setOpenedStage([value]);
  };

  const isTabEmpty = (formikKey: string) => {
    if (formikKey === "details") {
      return false;
    }

    const arrayKeys = ["hobbies", "interests", "asset_allocations", "investments"];
    if (arrayKeys.includes(formikKey)) {
      return !values[formikKey].length;
    }

    const filterKeys = ["industry_expertises", "asset_classes"];
    if (filterKeys.includes(formikKey)) {
      return !values[formikKey].some(
        (item: IProfileIndustryData | IProfileIndustryData) => item.operational || item.investment || item.learn_more
      );
    }
    return true;
  };

  const tabBuilder = (
    value: { stageNumber: string; formikKey: string; title: string; component: JSX.Element; description?: string },
    index: number
  ) => {
    const { stageNumber, formikKey, title, component, description } = value;

    return (
      <Box
        sx={{
          height: "56px",
          overflow: "hidden",
          display: "flex",
          ...(openedStage.includes(stageNumber) && {
            height: "fit-content"
          })
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", marginRight: "24px" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "2px 0",
              maxHeight: "14px",
              maxWidth: "14px",
              minHeight: "14px",
              minWidth: "14px",
              borderRadius: "9999px",
              border: "1px solid #30a6ad",
              ...(openedStage.includes(stageNumber) && {
                backgroundColor: "#30a6ad"
              })
            }}
          >
            {isTabEmpty(formikKey) ? null : <DoneIcon sx={{ color: "#30a6ad", fontSize: "11px" }} />}
          </Box>
          <Box
            sx={{
              height: "100%",
              width: "1px",
              backgroundColor: "#30a6ad",
              ...(index === tabs.length - 1 && {
                opacity: 0
              })
            }}
          ></Box>
        </Box>
        <Box sx={{ width: "486px" }}>
          <Typography
            sx={{
              color: "#0A3142",
              fontSize: "20px",
              cursor: "pointer",
              marginBottom: "5px",
              lineHeight: "0.9",
              fontWeight: 500
            }}
            onClick={() => {
              if (!isValid) return;
              if (openedStage.includes(stageNumber)) {
                setOpenedStage((prevStages: string[]) => prevStages.filter((stage) => stage !== stageNumber));
                return;
              }

              handleClick(stageNumber);
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              marginBottom: "24px",
              color: "#5D7782",

              transition: "opacity 0.5s ease-in-out",
              ...(openedStage.includes(stageNumber)
                ? {
                    visibility: "visible",
                    opacity: 1
                  }
                : {
                    visibility: "hidden",
                    opacity: 0
                  })
            }}
          >
            {description || "Select all that apply."}
          </Typography>
          <Box
            sx={{
              marginBottom: "24px",
              transition: "opacity 0.5s ease-in-out",
              ...(openedStage.includes(stageNumber)
                ? {
                    visibility: "visible",
                    opacity: 1
                  }
                : {
                    visibility: "hidden",
                    opacity: 0
                  })
            }}
          >
            {component}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Modal
      onClose={onClose}
      open={isOpen}
      sx={{ width: "100%", height: "100%", backgroundColor: "#1d3d4c" }}
      // PaperProps={{ component: "div" }}
    >
      {saveDataLoader ? (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            backgroundColor: "#F6F8FC",
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          <CircularProgress size={200} />
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            backgroundColor: "#F6F8FC",
            overflow: "scroll"
          }}
        >
          <Box
            sx={{
              position: "sticky",
              top: "0",
              zIndex: "2",
              height: "54px",
              width: "100%",
              backgroundColor: "#edf1f9",
              padding: "0px 24px 0px 32px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "55px"
            }}
          >
            <Typography> Edit Profile</Typography>
            <Box>
              <Button onClick={onClose} variant="text">
                Cancel
              </Button>
              <Button disabled={!isValid} onClick={() => handleSubmit()} variant="contained">
                Save
              </Button>
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <FormikProvider value={formik}>{tabs.map((value, index) => tabBuilder(value, index))}</FormikProvider>
          </Box>
        </Box>
      )}
    </Modal>
  );
};
